import {
  useNavigationContainerRef,
  useRootNavigationState,
  useRouter,
  type Href,
} from "@-/com.npmjs.expo-router";
import { useCallback, useMemo } from "react";

import { canGoBackWithinTabScope } from "#can-go-back-within-tab-scope";

/**
 * The one sanctioned way to perform "back" from back-control UI (header chevron, in-content
 * "Go back" buttons). Guarantees a back press can never cross the bottom-tab boundary:
 *
 * - `canGoBack` — reactive: `true` only when `router.back()` would pop a screen within the
 *   focused bottom tab (or within the plain stack for screens outside the tab navigator, e.g.
 *   auth/terms). Use it to gate whether a bare chevron renders at all.
 * - `goBack` — re-checks the LIVE navigation state at press time, then either pops in scope or
 *   navigates to `fallbackHref` when there is nothing in-scope to pop. Without a fallback it is
 *   a no-op — it never dispatches a GO_BACK that some ancestor (root stack, tab history) would
 *   "handle" by teleporting the user across tabs, and never surfaces the dev "GO_BACK was not
 *   handled" toast.
 */
export function useScopedBack(): {
  canGoBack: boolean;
  goBack: (options?: { fallbackHref?: Href }) => void;
} {
  const router = useRouter();
  const rootNavigationState = useRootNavigationState();
  const navigationContainerRef = useNavigationContainerRef();

  const canGoBack = useMemo(
    () => canGoBackWithinTabScope(rootNavigationState),
    [rootNavigationState],
  );

  const goBack = useCallback(
    (options?: { fallbackHref?: Href }) => {
      // Prefer the live container state over the render-time snapshot: a queued double-press or
      // an in-flight transition can make the snapshot stale, and a stale `true` here is exactly
      // the unhandled/cross-tab GO_BACK this hook exists to prevent.
      const liveRootState: unknown = navigationContainerRef.isReady()
        ? navigationContainerRef.getRootState()
        : rootNavigationState;

      if (canGoBackWithinTabScope(liveRootState)) {
        router.back();
        return;
      }

      if (options?.fallbackHref !== undefined) {
        router.navigate(options.fallbackHref);
      }
    },
    [navigationContainerRef, rootNavigationState, router],
  );

  return { canGoBack, goBack };
}
